import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { useBuildRun } from '../../state/buildRun';
import { useSession } from '../../state/useSession';
import { EmptyState } from '../../ui';

/**
 * An app whose build has not landed, and the one tap that builds it.
 *
 * The build reserves this row BEFORE it starts (state/buildRun.ts), holding the idea, the name,
 * the plan and the look, so a build that failed — or a page that closed mid-build — leaves
 * something to come back to. Shown as the editor it would have been, the row was an empty
 * chat, a blank preview and a Publish button with nothing to publish. Its Try again puts the
 * saved choices back and builds them into THIS row, under the same key while the server can
 * still replay it, so a build that finished meanwhile comes back without a second charge.
 */
export default function UnfinishedBuild({ project }: { project: Project }) {
  const { user } = useSession();
  const status = useBuildRun((s) => s.status);
  const buildingId = useBuildRun((s) => s.projectId);
  const reason = (project.definition as AppDefinition).unfinished?.reason
    ?? 'This app has not been built yet. Your choices are saved — Finish building builds it from them.';

  if (status === 'running') {
    return buildingId === project.id ? (
      <EmptyState icon="build" title={project.name} body="Building it now — this fills in the moment it lands." />
    ) : (
      <EmptyState icon="build" title={project.name} body={`${reason} Another app is building first; try this one once it lands.`} />
    );
  }

  return (
    <EmptyState
      icon="build"
      title={project.name}
      body={reason}
      ctaTitle="Finish building"
      onCta={() => void useBuildRun.getState().resumeProject(project, user?.id ?? '')}
    />
  );
}
